// src/Export.js
import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography, Paper, Button, Divider, Alert, CircularProgress, Chip } from '@mui/material';
import * as XLSX from 'xlsx';
import { useAuth } from './AuthContext';
import { getItemsPage } from './firestore';

const toISODate = (d) => {
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// start of month (local)
const startOfMonthISO = (d = new Date()) => {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  return toISODate(x);
};

// add months to an ISO date, return ISO
const addMonthsISO = (iso, delta) => {
  const [y, m, d] = iso.split('-').map(Number);
  const x = new Date(y, m - 1, d);
  x.setMonth(x.getMonth() + delta);
  return toISODate(x);
};

// AU financial year: Jul 1 -> Jun 30
const thisFYRange = (now = new Date()) => {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0=Jan
  // if before July (m<6), FY started last year
  const startYear = m < 6 ? y - 1 : y;
  const from = `${startYear}-07-01`;
  const to = `${startYear + 1}-07-01`; // exclusive
  return { from, to, label: `FY ${startYear}/${String(startYear + 1).slice(-2)}` };
};

const lastFYRange = (now = new Date()) => {
  const thisFY = thisFYRange(now);
  const startYear = Number(thisFY.from.slice(0, 4)) - 1;
  const from = `${startYear}-07-01`;
  const to = `${startYear + 1}-07-01`;
  return { from, to, label: `FY ${startYear}/${String(startYear + 1).slice(-2)}` };
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Export = () => {
  const { currentUser } = useAuth();

  const [preset, setPreset] = useState('thisMonth'); // thisMonth | lastMonth | last3 | thisFY | lastFY | all
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const range = useMemo(() => {
    const now = new Date();

    if (preset === 'all') {
      return { from: null, to: null, label: 'All time' };
    }

    if (preset === 'thisMonth') {
      const from = startOfMonthISO(now);
      const to = addMonthsISO(from, 1);
      return { from, to, label: 'This month' };
    }

    if (preset === 'lastMonth') {
      const thisFrom = startOfMonthISO(now);
      const from = addMonthsISO(thisFrom, -1);
      const to = thisFrom;
      return { from, to, label: 'Last month' };
    }

    if (preset === 'last3') {
      const thisFrom = startOfMonthISO(now);
      const from = addMonthsISO(thisFrom, -2); // includes this month + prev 2
      const to = addMonthsISO(thisFrom, 1);
      return { from, to, label: 'Last 3 months' };
    }

    if (preset === 'thisFY') return thisFYRange(now);
    if (preset === 'lastFY') return lastFYRange(now);

    // fallback
    const from = startOfMonthISO(now);
    const to = addMonthsISO(from, 1);
    return { from, to, label: 'This month' };
  }, [preset]);

  const buildFilters = useCallback(() => {
    // We export "income PAID", so we filter by paidAt (YYYY-MM-DD)
    const filters = [{ field: 'paid', op: '==', value: true }];

    // For non-all presets, filter by paidAt range
    if (range.from && range.to) {
      filters.push({ field: 'paidAt', op: '>=', value: range.from });
      filters.push({ field: 'paidAt', op: '<', value: range.to }); // exclusive upper bound
    }

    return filters;
  }, [range.from, range.to]);

  const exportPaidInvoices = useCallback(async () => {
    if (!currentUser) {
      window.location.href = '/login';
      return;
    }

    setMsg(null);
    setBusy(true);

    try {
      // Page through ALL matching invoices (so export is complete)
      let all = [];
      let cursor = null;
      let hasMore = true;

      while (hasMore) {
        const { items, lastDoc, hasMore: hm } = await getItemsPage({
          uid: currentUser.uid,
          collectionName: 'invoices',
          filters: buildFilters(),
          order: { field: 'paidAt', dir: 'desc' },
          pageSize: 500,
          cursor,
        });

        all = all.concat(items || []);
        cursor = lastDoc || null;
        hasMore = !!hm;
      }

      if (!all.length) {
        setMsg({ type: 'info', text: `No paid invoices found for: ${range.label}.` });
        return;
      }

      // Build rows
      const rows = all.map((inv) => ({
        'Paid Date': inv.paidAt || '',
        'Invoice #': inv.invoiceNumber != null ? `INV-${String(inv.invoiceNumber).padStart(4, '0')}` : '',
        Participant: inv.participantSnapshot?.name || '',
        'Issue Date': inv.date || '',
        'Due Date': inv.dueDate || '',
        Total: Number(inv.totalAmount || 0),
      }));

      const totalPaid = rows.reduce((sum, r) => sum + Number(r.Total || 0), 0);

      const summary = [
        { Field: 'Range', Value: range.label },
        { Field: 'From', Value: range.from || '—' },
        { Field: 'To (exclusive)', Value: range.to || '—' },
        { Field: 'Paid invoices count', Value: String(rows.length) },
        { Field: 'Total paid income', Value: money(totalPaid) },
        { Field: 'Generated', Value: new Date().toLocaleString() },
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();

      const ws1 = XLSX.utils.json_to_sheet(rows);
      // Format totals column as numbers (best effort)
      // (Excel will usually infer, but this helps)
      const totalCol = 'F'; // Paid Date(A) Invoice#(B) Participant(C) Issue(D) Due(E) Total(F)
      for (let i = 2; i <= rows.length + 1; i++) {
        const cell = ws1[`${totalCol}${i}`];
        if (cell) cell.t = 'n';
      }
      XLSX.utils.book_append_sheet(wb, ws1, 'Paid invoices');

      const ws2 = XLSX.utils.json_to_sheet(summary);
      XLSX.utils.book_append_sheet(wb, ws2, 'Summary');

      const fileName = `dashflow-paid-invoices-${preset}-${toISODate(new Date())}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setMsg({ type: 'success', text: `Exported ${rows.length} paid invoices (${money(totalPaid)}).` });
    } catch (e) {
      console.error(e);
      setMsg({
        type: 'error',
        text:
          e?.message ||
          'Export failed. If Firestore asks for an index, open the link it provides and create it (one click).',
      });
    } finally {
      setBusy(false);
    }
  }, [currentUser, buildFilters, range.label, range.from, range.to, preset]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
        Export
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        Export paid invoices to Excel (filtered by <strong>Paid Date</strong>).
      </Typography>

      {msg && (
        <Alert severity={msg.type} sx={{ mb: 2 }}>
          {msg.text}
        </Alert>
      )}

      <Paper sx={{ p: 2.5, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 900 }}>Preset</Typography>
          <Chip label={range.label} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant={preset === 'thisMonth' ? 'contained' : 'outlined'} onClick={() => setPreset('thisMonth')}>
            This month
          </Button>
          <Button variant={preset === 'lastMonth' ? 'contained' : 'outlined'} onClick={() => setPreset('lastMonth')}>
            Last month
          </Button>
          <Button variant={preset === 'last3' ? 'contained' : 'outlined'} onClick={() => setPreset('last3')}>
            Last 3 months
          </Button>
          <Button variant={preset === 'thisFY' ? 'contained' : 'outlined'} onClick={() => setPreset('thisFY')}>
            This FY
          </Button>
          <Button variant={preset === 'lastFY' ? 'contained' : 'outlined'} onClick={() => setPreset('lastFY')}>
            Last FY
          </Button>
          <Button variant={preset === 'all' ? 'contained' : 'outlined'} onClick={() => setPreset('all')}>
            All time
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Button
          variant="contained"
          onClick={exportPaidInvoices}
          disabled={busy}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
        >
          {busy ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Exporting…
            </>
          ) : (
            'Export paid invoices (Excel)'
          )}
        </Button>

        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
         
        </Typography>
      </Paper>
    </Box>
  );
};

export default Export;
